// Calculator.net-style shared UI helpers used by the "Other" category pixel-align rebuilds.
// NOTE: Per user request, the gray input-panel background and inline Calculate/Clear
// buttons were removed globally. CNetActions is now an invisible auto-runner: it fires
// `onCalc` on mount and again after any input change inside the same CalcShell body.
// This preserves the results-display UX from every existing calculator without editing
// each file to add explicit auto-compute effects.
import React, { useEffect, useRef } from 'react';

export function CNetSection({ title, children }) {
  return (
    <section className="mb-2">
      {title && <h3 className="cnet-section-title">{title}</h3>}
      <div className="cnet-panel">{children}</div>
    </section>
  );
}

export function CNetRow({ label, children, threeCol = true }) {
  return (
    <div className={threeCol ? 'cnet-row' : 'cnet-row-2col'}>
      <div className="cnet-label">{label}</div>
      {children}
    </div>
  );
}

// Invisible auto-runner. Replaces the old Calculate/Clear button pair.
export function CNetActions({ onCalc }) {
  const onCalcRef = useRef(onCalc);
  onCalcRef.current = onCalc;
  const anchorRef = useRef(null);

  useEffect(() => {
    // Initial compute so results show on first mount
    const t0 = setTimeout(() => onCalcRef.current && onCalcRef.current(), 0);

    // Find nearest CalcShell body so we only listen to inputs inside our own calculator
    const anchor = anchorRef.current;
    let root = anchor && anchor.closest('.calc-body');
    if (!root) root = document;

    let pending = null;
    const trigger = () => {
      // Defer to macrotask so React commits input state before we recompute
      if (pending) return;
      pending = setTimeout(() => {
        pending = null;
        if (onCalcRef.current) onCalcRef.current();
      }, 0);
    };
    root.addEventListener('input', trigger, false);
    root.addEventListener('change', trigger, false);
    return () => {
      clearTimeout(t0);
      if (pending) clearTimeout(pending);
      root.removeEventListener('input', trigger, false);
      root.removeEventListener('change', trigger, false);
    };
  }, []);

  // Invisible anchor node used to locate the surrounding .calc-body
  return <span ref={anchorRef} style={{ display: 'none' }} aria-hidden="true" />;
}

export function CNetHintBar({ text = 'Enter values below to calculate.' }) {
  return (
    <div className="cnet-hint-bar">
      <span>{text}</span>
    </div>
  );
}

export function CNetResult({ children, title = 'Result' }) {
  return (
    <>
      <div className="cnet-result-hdr">{title}</div>
      <div className="cnet-result-body">{children}</div>
    </>
  );
}

export const G = (n, d = 2) =>
  isFinite(n) ? Number(n).toLocaleString(undefined, { maximumFractionDigits: d }) : '—';
